import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ShoppingBag, User, Search, Menu, Heart } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from './ui/dialog';
import { useCart } from '../contexts/CartContext';
import { useAuth } from '../contexts/AuthContext';
import CartSheet from './CartSheet';
import { CUSTOM_ORDERS_ENABLED } from '../lib/shop';
import { ReactComponent as LogoMark } from '../assets/logo-placard-doudous.svg';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import { LanguageSwitcher } from '../i18n/LocaleSwitch';
import { translatePath } from '../i18n/routes';

const EASE = [0.22, 1, 0.36, 1];

const COPY = {
  fr: {
    shop: 'Boutique', custom: 'Sur mesure', story: 'Notre Histoire', journal: 'Journal', contact: 'Contact',
    announce: 'Fait main en France\u2002·\u2002Livraison suivie', announceCustom: ' · sur mesure en 10–15\u00a0j',
    mainNav: 'Navigation principale', searchLabel: 'Rechercher un compagnon', searchPlaceholder: 'Rechercher un compagnon…',
    search: 'Rechercher', account: 'Compte', cart: 'Panier', openMenu: 'Ouvrir le menu',
    band: ['Doux', 'Uniques', 'Pour les grands rêveurs'], bandRight: 'Faits main en France',
    menuTitle: 'Navigation', menuDescription: 'Accédez à la boutique, à votre compte et aux informations de l’atelier.', mobileNav: 'Navigation mobile',
    myAccount: 'Mon compte', menuFooter: 'Fait main en France — Atelier de Margot',
    issue: 'Hors-série', issueName: 'Les Naissances d’août', menuKicker: 'Au sommaire',
  },
  en: {
    shop: 'Shop', custom: 'Made to order', story: 'Our Story', journal: 'Journal', contact: 'Contact',
    announce: 'Handmade in France\u2002·\u2002Tracked shipping', announceCustom: ' · made to order in 10–15\u00a0days',
    mainNav: 'Main navigation', searchLabel: 'Search for a companion', searchPlaceholder: 'Search for a companion…',
    search: 'Search', account: 'Account', cart: 'Bag', openMenu: 'Open menu',
    band: ['Soft', 'One of a kind', 'For big dreamers'], bandRight: 'Handmade in France',
    menuTitle: 'Navigation', menuDescription: 'Go to the shop, your account and the workshop information.', mobileNav: 'Mobile navigation',
    myAccount: 'My account', menuFooter: 'Handmade in France — Margot’s workshop',
    issue: 'Special issue', issueName: 'Born in August', menuKicker: 'In this issue',
  },
};

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { count, isOpen, setIsOpen } = useCart();
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const c = useCopy(COPY);
  const { lp } = useLocale();
  // Comparaisons faites sur l'adresse française, quelle que soit la langue affichée.
  const frPath = translatePath(location.pathname, 'fr');
  const isConfigurator = ['/sur-mesure', '/personalisation'].includes(frPath);
  const isAtelier = isConfigurator || ['/boutique', '/panier', '/compte', '/contact', '/connexion', '/inscription'].includes(frPath) || frPath.startsWith('/produit/');
  // Sur la couverture, la barre s'efface : le titre du magazine tient lieu de logo.
  const onCover = frPath === '/' && !scrolled;
  const [navSearch, setNavSearch] = useState('');

  useEffect(() => {
    // Sur l'accueil, la couverture reste épinglée pendant que son livre s'anime : la barre
    // garde son allure de couverture jusqu'à ce que la couverture se remette à défiler.
    const handler = () => {
      const cover = document.getElementById('couverture');
      setScrolled((cover ? -cover.getBoundingClientRect().top : window.scrollY) > 20);
    };
    handler();
    window.addEventListener('scroll', handler);
    return () => window.removeEventListener('scroll', handler);
  }, []);

  useEffect(() => { setMenuOpen(false); }, [location]);

  useEffect(() => {
    const media = window.matchMedia('(min-width: 1024px)');
    const closeOnDesktop = () => { if (media.matches) setMenuOpen(false); };
    media.addEventListener('change', closeOnDesktop);
    return () => media.removeEventListener('change', closeOnDesktop);
  }, []);

  const navLinks = [
    { to: '/boutique', id: 'boutique', label: c.shop },
    ...(CUSTOM_ORDERS_ENABLED ? [{ to: '/sur-mesure', id: 'sur-mesure', label: c.custom }] : []),
    { to: '/a-propos', id: 'notre-histoire', label: c.story },
    { to: '/journal', id: 'journal', label: c.journal },
    { to: '/contact', id: 'contact', label: c.contact },
  ];

  return (
    <Dialog open={menuOpen} onOpenChange={setMenuOpen}>
      <header className={`fixed top-0 left-0 right-0 z-50${isAtelier ? ' atelier-navbar' : ''}`}>
        {/* Annonce bar — disparaît au scroll */}
        <AnimatePresence initial={false}>
          {!scrolled && !isConfigurator && !isAtelier && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="overflow-hidden bg-[hsl(var(--foreground))] text-[hsl(var(--brand-butter-light))]"
            >
              <p className="px-3 py-2 text-center font-sans text-[11px] font-medium tracking-[0.14em] uppercase">
                {c.announce}{CUSTOM_ORDERS_ENABLED && c.announceCustom}
              </p>
            </motion.div>
          )}
        </AnimatePresence>

        <div
          className={`${isAtelier ? 'atelier-nav-surface ' : ''}transition-[background-color,border-color] duration-300 border-b ${
            scrolled || isConfigurator
              ? 'bg-card/90 backdrop-blur-md border-border'
              : onCover
                ? 'bg-transparent border-transparent'
                : 'bg-gradient-to-b from-[hsl(var(--background)/0.92)] via-[hsl(var(--background)/0.55)] to-transparent border-transparent'
          }`}
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
            <div className={`flex items-center justify-between h-16 sm:h-[4.5rem]${isAtelier ? ' atelier-nav-inner' : ''}`}>
              {/* Logo */}
              <Link to={lp('/')} className={`group flex shrink-0 items-center gap-3 leading-none text-foreground${isAtelier ? ' atelier-nav-logo' : ''}`} data-testid="nav-logo" aria-label="Le Placard à Doudous">
                <LogoMark className="h-10 w-8 transition-transform duration-300 group-hover:-rotate-3 sm:h-11 sm:w-9" aria-hidden="true" focusable="false" />
                {onCover ? (
                  <span className="font-sans text-[11px] font-extrabold uppercase leading-none tracking-[0.16em]" data-testid="nav-issue">
                    {c.issue}<span className="hidden sm:inline lg:hidden xl:inline">{'\u2002·\u2002'}{c.issueName}</span>
                  </span>
                ) : (
                  <span className="flex flex-col gap-1">
                    <span className="font-serif text-sm font-bold leading-none tracking-[-0.01em] min-[375px]:text-base sm:text-lg">
                      Le Placard à Doudous
                    </span>
                  </span>
                )}
              </Link>

              {/* Desktop nav */}
              <nav className={`hidden lg:flex items-center gap-6${isAtelier ? ' atelier-nav-links' : ''}`} aria-label={c.mainNav}>
                {navLinks.map(link => (
                  <Link
                    key={link.to}
                    to={lp(link.to)}
                    aria-current={frPath.startsWith(link.to) || (link.to === '/boutique' && frPath.startsWith('/produit/')) ? 'page' : undefined}
                    data-testid={`main-nav-${link.id}-link`}
                    className={`font-sans text-[12px] font-bold uppercase tracking-[0.14em] transition-colors duration-200 relative group ${
                      frPath.startsWith(link.to)
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground'
                    }`}
                  >
                    {link.label}
                    <span className={`absolute -bottom-1.5 left-0 h-[2px] bg-primary transition-[width] duration-300 ${
                      frPath.startsWith(link.to) ? 'w-full' : 'w-0 group-hover:w-full'
                    }`} />
                  </Link>
                ))}
              </nav>

              {/* Actions */}
              <div className={`flex items-center gap-0.5 sm:gap-1${isAtelier ? ' atelier-nav-actions' : ''}`}>
                <LanguageSwitcher className="hidden lg:inline-flex mr-2" />
                {isAtelier && <form className="atelier-nav-search" role="search" onSubmit={event => {
                  event.preventDefault(); navigate(`${lp('/boutique')}${navSearch.trim() ? `?search=${encodeURIComponent(navSearch.trim())}` : ''}`);
                }}>
                  <Search size={14} aria-hidden="true" />
                  <input aria-label={c.searchLabel} placeholder={c.searchPlaceholder} value={navSearch} onChange={event => setNavSearch(event.target.value)} />
                </form>}
                <Button asChild variant="ghost" size="icon" className={`hidden sm:inline-flex rounded-full text-foreground hover:bg-secondary transition-colors duration-200${isAtelier ? ' atelier-search-shortcut' : ''}`}>
<Link to={lp('/boutique')} aria-label={c.search}>
                    <Search className="h-4 w-4" strokeWidth={1.75} />
                  </Link>
</Button>
                <Button asChild variant="ghost" size="icon" className="hidden sm:inline-flex rounded-full text-foreground hover:bg-secondary transition-colors duration-200">
<Link to={lp(user ? '/compte' : '/connexion')} aria-label={c.account} data-testid="nav-account-link">
                    <User className="h-4 w-4" strokeWidth={1.75} />
                  </Link>
</Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="rounded-full text-foreground hover:bg-secondary transition-colors duration-200 relative"
                  onClick={() => setIsOpen(true)}
                  aria-label={c.cart}
                  data-testid="nav-cart-button"
                >
                  <ShoppingBag className="h-4 w-4" strokeWidth={1.75} />
                  {count > 0 && (
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute -top-0.5 -right-0.5 bg-primary text-primary-foreground text-[10px] font-semibold w-4 h-4 rounded-full flex items-center justify-center"
                    >
                      {count}
                    </motion.span>
                  )}
                </Button>
                {/* Mobile menu toggle */}
                <DialogTrigger asChild><Button
                  variant="ghost"
                  size="icon"
                  className="lg:hidden rounded-full text-foreground hover:bg-secondary transition-colors duration-200"
                  aria-label={c.openMenu}
                  aria-expanded={menuOpen}
                >
                  <Menu className="h-4 w-4" strokeWidth={1.75} />
                </Button></DialogTrigger>
              </div>
            </div>
          </div>
        </div>

        {/* Bandeau cacao — pages atelier, desktop */}
        {isAtelier && (
          <div className="atelier-band">
            <div className="atelier-band-inner">
              <p>{c.band.map((word, i) => <React.Fragment key={word}>{i > 0 && <span>·</span>}{word}</React.Fragment>)}</p>
              <p>{c.bandRight} <Heart size={13} strokeWidth={1.5} /></p>
            </div>
          </div>
        )}

        {/* Menu mobile plein écran */}
        <DialogContent className="lg:hidden inset-0 top-0 left-0 max-w-none translate-x-0 translate-y-0 h-dvh flex flex-col gap-0 justify-start sm:justify-center overflow-y-auto border-0 sm:rounded-none bg-[hsl(var(--brand-butter))] px-8 pt-16 pb-8" data-lenis-prevent>
              <DialogTitle className="sr-only">{c.menuTitle}</DialogTitle>
              <DialogDescription className="sr-only">{c.menuDescription}</DialogDescription>
              <p className="kicker self-start">{c.menuKicker}</p>
              <nav className="mt-4 flex flex-col gap-1" aria-label={c.mobileNav}>
                {navLinks.map((link, i) => (
                  <motion.div
                    key={link.to}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: 0.08 + i * 0.07, ease: EASE }}
                  >
                    <Link
                      to={lp(link.to)}
                      className="block py-2.5 font-serif text-4xl font-extrabold tracking-[-0.02em] text-foreground transition-colors duration-200 hover:text-primary"
                    >
                      {link.label}
                    </Link>
                  </motion.div>
                ))}
              </nav>
              <div className="sm:hidden mt-4 pt-4 border-t-2 border-foreground flex items-center gap-6 font-sans text-sm font-semibold">
                <Link to={lp('/boutique')} className="inline-flex min-h-11 items-center gap-2"><Search size={16} /> {c.search}</Link>
                <Link to={lp(user ? '/compte' : '/connexion')} className="inline-flex min-h-11 items-center gap-2"><User size={16} /> {c.myAccount}</Link>
              </div>
              <LanguageSwitcher className="mt-6 self-start text-sm" onSwitch={() => setMenuOpen(false)} />
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="mt-10 font-sans text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground"
              >
                {c.menuFooter}
              </motion.p>
        </DialogContent>
      </header>

      <CartSheet open={isOpen} onOpenChange={setIsOpen} />
    </Dialog>
  );
}
